import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { documentApi } from '../services/api';
import { creditApi } from '../services/creditApi';
import { httpClient } from '../services/httpClient';
import { LegacyCase } from '../types';

interface ClaimantBackgroundSection {
  sectionId: string;
  title: string;
  narrative: string;
  citations?: any[];
}

interface MetadataExtractionResponse {
  patients: any[];
  keyDates: any[];
  documentTypes: any[];
  pertinentFacts?: Record<string, any[]>;
  medicalTerms?: any[];
  caseSummary?: string;
  caseSummaryCitations?: any[];
  claimantBackground?: ClaimantBackgroundSection[];
  confidence: number;
  reasoning: string;
  totalInputTokens?: number;
  totalOutputTokens?: number;
  estimatedCostUsd?: number;
  truncated?: boolean;
  durationSeconds?: number;
  status?: 'COMPLETE' | 'PARTIAL' | 'FAILED';
  warnings?: string[];
  phaseBreakdown?: {
    phase: string;
    durationSeconds: number;
    inputTokens: number;
    outputTokens: number;
    estimatedCostUsd: number;
    parallel: boolean;
    chunkCount?: number;
    subPhases?: any[];
    modelHint?: string;
  }[];
  aiVisionCostUsd?: number;
  documentScanCostUsd?: number;
  ocrPageCount?: number;
  completedAt?: string;
  expertEvidence?: ExpertEvidenceSummary[];
}

export interface ExpertEvidenceSummary {
  documentId: string;
  documentName: string;
  expertName?: string;
  qualifications?: string;
  speciality?: string;
  instructedBy?: string;
  examinationDate?: string;
  reportDate?: string;
  opinions?: ExpertOpinion[];
  conclusions?: string;
  citations?: any[];
}

export interface ExpertOpinion {
  topic?: string;
  opinion?: string;
  reasoning?: string;
  citations?: any[];
}

export interface CreditShortfall {
  balance: number;
  required: number;
}

export interface UseMetadataExtractionReturn {
  metadata: MetadataExtractionResponse | null;
  hasMetadata: boolean;
  documents: any[];
  hasDocuments: boolean;
  extracting: boolean;
  analysisInProgress: boolean;
  loadingMetadata: boolean;
  error: string | null;
  creditShortfall: CreditShortfall | null;
  queued: boolean;
  queuedPageLimit: number | null;
  warnings: string[];
  isPartial: boolean;
  handleExtractMetadata: () => void;
  handleAnalysisComplete: () => void;
  handleCancelAnalysis: () => void;
  dismissCreditShortfall: () => void;
}

export function useMetadataExtraction(caseId: string, caseData?: LegacyCase, persistedStatus?: string): UseMetadataExtractionReturn {
  // Seed backendRunning from the persisted tab status so we don't flash
  // "Processed" while the async progress check is still in flight.
  const wasAnalysing = persistedStatus === 'ANALYSING' || persistedStatus === 'PROCESSING';
  const [extracting, setExtracting] = useState(false);
  const [backendRunning, setBackendRunning] = useState(wasAnalysing);
  const [checkingProgress, setCheckingProgress] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creditShortfall, setCreditShortfall] = useState<CreditShortfall | null>(null);
  const [queued, setQueued] = useState(caseData?.status === 'QUEUED');
  const [queuedPageLimit, setQueuedPageLimit] = useState<number | null>(null);
  const queryClient = useQueryClient();

  // Reset local state when caseId changes (e.g. switching via tab or creating new case)
  useEffect(() => {
    setExtracting(false);
    setBackendRunning(wasAnalysing);
    setCheckingProgress(true);
    setError(null);
    setQueued(caseData?.status === 'QUEUED');
    setQueuedPageLimit(null);
  }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear queued flag when backend status moves past QUEUED (e.g. admin releases the case)
  useEffect(() => {
    if (caseData?.status && caseData.status !== 'QUEUED' && queued) {
      setQueued(false);
      setQueuedPageLimit(null);
    }
  }, [caseData?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if backend analysis is already running (survives HTTP timeouts and page refreshes)
  useEffect(() => {
    let cancelled = false;
    const checkProgress = async () => {
      try {
        const data = await httpClient.get<{ complete: boolean; failed: boolean }>(
          `/intelligence/metadata/progress/${caseId}`
        );
        if (!cancelled) {
          if (data && !data.complete && !data.failed) {
            setBackendRunning(true);
          } else {
            setBackendRunning(false);
          }
        }
      } catch {
        // 204 or network error — no active analysis
        if (!cancelled) setBackendRunning(false);
      } finally {
        if (!cancelled) setCheckingProgress(false);
      }
    };
    checkProgress();
    return () => { cancelled = true; };
  }, [caseId]);

  // Metadata query
  const { data: metadata, isLoading, isFetching } = useQuery<MetadataExtractionResponse | null>(
    ['metadata', caseId],
    async () => {
      try {
        const data = await httpClient.get<MetadataExtractionResponse>(
          `/intelligence/metadata/${caseId}`
        );
        if (data && typeof data === 'object') {
          return data;
        }
      } catch (err: any) {
        const message = err?.message || 'Failed to load saved metadata';
        console.error('Failed to load saved metadata:', err);
        setError(message);
      }

      return null;
    },
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnMount: 'always',
    }
  );

  // Treat as loading when cache holds null but a refetch is in flight —
  // prevents premature redirect to 'records' when metadata now exists on server.
  const loadingMetadata = isLoading || (isFetching && !metadata);

  // Documents query
  const { data: documentsData } = useQuery(
    ['documents', caseId],
    () => documentApi.getCaseDocuments(caseId),
    { refetchOnMount: 'always', refetchOnWindowFocus: true }
  );

  const documents = documentsData || [];
  const hasDocuments = documents.length > 0;
  const hasMetadata = !!(metadata && (
    (metadata.patients?.length || 0) > 0 ||
    (metadata.keyDates?.length || 0) > 0 ||
    (metadata.documentTypes?.length || 0) > 0 ||
    (metadata.pertinentFacts && Object.values(metadata.pertinentFacts).some(arr => arr.length > 0)) ||
    (metadata.medicalTerms?.length || 0) > 0 ||
    (metadata.claimantBackground?.length || 0) > 0
  ));

  async function triggerExtraction() {
    if (!documents || documents.length === 0) {
      return;
    }

    setExtracting(true);
    try {
      const instructionLetterDocId = caseData?.instructionLetterDocumentId;
      const documentIds = documents
        .map((doc: any) => doc.documentId)
        .filter((id: string) => id !== instructionLetterDocId);

      const totalPages = documents.reduce((sum: number, d: any) => sum + (d.pageCount || 0), 0);

      // Fire-and-forget: server returns 202 immediately and processes in background.
      // Progress is tracked via polling; results fetched via GET after completion.
      const response = await httpClient.post<{ status: string; pageLimit?: number }>('/intelligence/metadata/extract', {
        caseId,
        documentIds,
        totalPages,
        caseTypes: caseData?.caseTypes,
        processingGuidance: caseData?.processingGuidance,
        incidentDateStart: caseData?.incidentDateStart,
        incidentDateEnd: caseData?.incidentDateEnd,
        instructionLetterData: caseData?.instructionLetterData ? JSON.stringify(caseData.instructionLetterData) : undefined,
        caseName: caseData?.name,
      });

      // Handle queued response — case exceeds page limit gate
      if (response?.status === 'queued') {
        setQueued(true);
        setQueuedPageLimit(response.pageLimit ?? null);
        setExtracting(false);
        setBackendRunning(false);
        return;
      }
    } catch (err: any) {
      const message = err?.message || 'Failed to start analysis';
      console.error('Failed to start analysis:', err);
      setError(message);
      setExtracting(false);
      setBackendRunning(false);
    }
  }

  const handleExtractMetadata = async () => {
    if (!documents || documents.length === 0) {
      setError('Please upload documents first before extracting metadata.');
      return;
    }

    setError(null);
    setCreditShortfall(null);

    // Pre-flight credit check — we already know page counts from uploaded documents
    const totalPages = documents.reduce((sum: number, d: any) => sum + (d.pageCount || 0), 0);
    if (totalPages > 0) {
      try {
        const creditBalance = await creditApi.getBalance();
        // Subscribers always proceed (consumption billing with overages)
        if (creditBalance.hasActiveSubscription) {
          // OK — proceed
        } else if (creditBalance.accountType === 'EARLY_ADOPTER') {
          // Early adopters: free credits only, blocked when insufficient
          if (creditBalance.freePageBalance < totalPages) {
            setCreditShortfall({ balance: creditBalance.freePageBalance, required: totalPages });
            return;
          }
        } else {
          // STANDARD without subscription — shouldn't happen, but let backend handle it
        }
      } catch {
        // If balance check fails, let the analysis proceed — backend will catch it
      }
    }

    setBackendRunning(true);
    triggerExtraction();
  };

  const handleAnalysisComplete = () => {
    setExtracting(false);
    setBackendRunning(false);
    setQueued(false);
    setQueuedPageLimit(null);
    queryClient.invalidateQueries(['metadata', caseId]);
  };

  const handleCancelAnalysis = () => {
    httpClient.post(`/intelligence/metadata/cancel/${caseId}`, {}).catch(() => {
      // Best-effort — polling will pick up the cancelled state
    });
  };

  // While the initial progress check is in flight, keep analysisInProgress true
  // if the persisted tab status said we were analysing — prevents flash to "Processed".
  const analysisInProgress = extracting || backendRunning || (checkingProgress && wasAnalysing);
  const isPartial = metadata?.status === 'PARTIAL';
  const warnings = metadata?.warnings ?? [];

  const dismissCreditShortfall = () => setCreditShortfall(null);

  return {
    metadata,
    hasMetadata,
    documents,
    hasDocuments,
    extracting,
    analysisInProgress,
    loadingMetadata,
    error,
    creditShortfall,
    queued,
    queuedPageLimit,
    warnings,
    isPartial,
    handleExtractMetadata,
    handleAnalysisComplete,
    handleCancelAnalysis,
    dismissCreditShortfall,
  };
}
